import { useCallback, useState } from "react"
import { useSearch } from "@tanstack/react-router"
import { z } from "zod"

import { convertHtmlToText } from "@productlane/lib/src/browser"
import {
	Empty,
	Form,
	FormTextarea,
	SubmitButton,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	useZodForm,
} from "@productlane/ui"

import { api } from "@/lib/api"
import { PrefillRow } from "./prefill-row"

export interface HubspotContentProps {
	notes: Array<{ text: string }>
	emails: Array<{ text: string }>
	calls: Array<{ text: string }>
	meetings: Array<{ text: string }>
	userEmail: string | null
}

export function HubspotContent({
	notes,
	emails,
	calls,
	meetings,
	userEmail,
}: HubspotContentProps) {
	const search = useSearch({ strict: false })
	const form = useZodForm({ schema: z.object({ text: z.string().min(1) }) })
	const token = search.token ?? null
	const objectId = search.objectId ?? null
	const objectType = search.objectType ?? null
	const hubspotUrl = search.hubspotUrl ?? null
	const hubspotLink = `<a href="${hubspotUrl}">View in Hubspot</a><br />`
	const prefill = useCallback(
		(text: string) => {
			const existing = form.getValues().text
			if (existing) {
				form.setValue("text", existing + "\n" + convertHtmlToText(text))
			} else {
				form.setValue("text", convertHtmlToText(text))
			}
		},
		[form],
	)
	const createFeedbackMutation = api.hubspot.createFeedback.useMutation()
	const [tabValue, setTabValue] = useState("notes")
	return (
		<>
			<Tabs value={tabValue} onValueChange={setTabValue}>
				<TabsList className="mb-4" id="hubspot-tabs">
					<TabsTrigger value="notes">Notes</TabsTrigger>
					<TabsTrigger value="emails">Emails</TabsTrigger>
					<TabsTrigger value="calls">Calls</TabsTrigger>
					<TabsTrigger value="meetings">Meetings</TabsTrigger>
				</TabsList>
				<TabsContent
					value="notes"
					className="max-h-[300px] space-y-1 overflow-auto"
				>
					{notes.length === 0 ? (
						<Empty text="No notes found" />
					) : (
						notes.map((note, i) => (
							<PrefillRow
								onClick={() => {
									prefill(note.text)
								}}
								key={i}
							>
								{convertHtmlToText(note.text)}
							</PrefillRow>
						))
					)}
				</TabsContent>

				<TabsContent
					value="emails"
					className="max-h-[300px] space-y-1 overflow-auto"
				>
					{emails.length === 0 ? (
						<Empty text="No emails found" />
					) : (
						emails.map((email, i) => (
							<PrefillRow
								onClick={() => {
									prefill(email.text)
								}}
								key={i}
							>
								{convertHtmlToText(email.text)}
							</PrefillRow>
						))
					)}
				</TabsContent>

				<TabsContent
					value="calls"
					className="max-h-[300px] space-y-1 overflow-auto"
				>
					{calls.length === 0 ? (
						<Empty text="No calls found" />
					) : (
						calls.map((call, i) => (
							<PrefillRow
								onClick={() => {
									prefill(call.text)
								}}
								key={i}
							>
								{convertHtmlToText(call.text)}
							</PrefillRow>
						))
					)}
				</TabsContent>

				<TabsContent
					value="meetings"
					className="max-h-[300px] space-y-1 overflow-auto"
				>
					{meetings.length === 0 ? (
						<Empty text="No meetings found" />
					) : (
						meetings.map((meeting, i) => (
							<PrefillRow
								onClick={() => {
									prefill(meeting.text)
								}}
								key={i}
							>
								{convertHtmlToText(meeting.text)}
							</PrefillRow>
						))
					)}
				</TabsContent>
			</Tabs>
			<hr className="my-4" />
			<Form
				form={form}
				onSubmit={async (values) => {
					if (!objectId || !objectType || !token) {
						throw new Error("Something went wrong")
					}
					const text = values.text.replace(/\n/g, "<br />")
					await createFeedbackMutation.mutateAsync({
						text: hubspotLink + text,
						token,
						objectId,
						objectType,
						userEmail,
					})
					if (typeof window !== "undefined") {
						window.parent.postMessage(JSON.stringify({ action: "DONE" }), "*")
					}
					return
				}}
			>
				<FormTextarea
					name="text"
					minRows={9}
					placeholder="Capture meeting notes, transcripts, ad-hoc feedback and more"
				/>
				<div className="mt-4 flex justify-end gap-4">
					<SubmitButton className="cursor-pointer" size="xl">
						Submit
					</SubmitButton>
				</div>
			</Form>
		</>
	)
}
